import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Palmtree,
  ExternalLink,
  LogIn,
  Home,
  Sparkles,
  Coins,
  Layers,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import IsoIsland from "./oasis/IsoIsland";
import api from "@/lib/api";
import { t } from "@/i18n";

function PublicNav() {
  return (
    <header className="border-b bg-card">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center font-head font-bold text-primary arabic">
            ع
          </div>
          <span className="font-head font-extrabold text-lg hidden sm:block">{t("navbar.harf")}</span>
        </Link>
        <Link
          to="/"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition ml-2"
        >
          <Home size={14} /> {t("dictionaryworden.home")}
        </Link>
        <div className="flex-1" />
        <Link
          to="/auth"
          className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
        >
          {t("publicislanden.get_started")}
        </Link>
      </div>
    </header>
  );
}

export default function PublicIslandEn() {
  const token = window.location.pathname.split("/fr").filter(Boolean).pop();
  const [state, setState] = useState({ loading: true, error: null, ownerName: "", island: null });
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    api
      .get(`/oasis/public/${token}`)
      .then((r) => {
        if (!alive) return;
        if (!r.data?.island?.chunks) throw new Error("island missing from response");
        setState({
          loading: false,
          error: null,
          ownerName: r.data.ownerName,
          island: r.data.island,
        });
      })
      .catch(() => {
        if (alive)
          setState({
            loading: false,
            error: "This link doesn't lead to any island — it may have expired.",
            ownerName: "",
            island: null,
          });
      });
    return () => {
      alive = false;
    };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex flex-col">
        {!user && <PublicNav />}
        <div className="flex-1 grid place-items-center text-muted-foreground animate-pulse">
          {t("publicislanden.loading_the_island")}
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col">
        {!user && <PublicNav />}
        <div className="flex-1 grid place-items-center p-6 text-center">
          <div>
            <p className="text-lg font-semibold">{state.error}</p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary underline">
              {t("publicislanden.back_to_home")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {!user && <PublicNav />}

      <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3 border-b">
        <div className="flex items-center gap-2">
          <Palmtree size={20} className="text-primary" />
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t("publicislanden.oasis_of")}</p>
            <p className="font-head font-bold leading-tight">{state.ownerName}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {t("publicislanden.view_only_nothing_can_be_changed")}
        </span>
      </div>

      <div className="flex-1 min-h-0">
        <IsoIsland
          island={state.island}
          editMode={null}
          pending={null}
          paintGround="dirt"
          balance={0}
          chunkCost={null}
          movingUid={null}
          onPlace={() => {}}
          onSelectPlaced={() => {}}
          onBuyChunk={() => {}}
          onPaint={() => {}}
          selectedPlaced={null}
          calibrate={false}
          adminSky={{ phase: null, rain: null }}
          onDropItem={() => {}}
          onCancelAction={() => {}}
          readOnly
        />
      </div>

      {/* ── Incentive to join Harf ────────────────────────── */}
      <div className="border-t bg-card">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center">
          <Sparkles size={26} className="mx-auto text-primary mb-3" />
          <h2 className="font-head text-xl sm:text-2xl font-bold mb-2">{t("dictionaryworden.what_is_harf")}</h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-6 leading-relaxed">
            {t("publicislanden.an_app_for_learning_modern_standard")}
          </p>

          <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-7 text-left">
            <div className="rounded-2xl border bg-background p-4">
              <Coins size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicislanden.earn_dinars")}</p>
              <p className="text-xs text-muted-foreground">{t("publicislanden.by_learning_every_day")}</p>
            </div>
            <div className="rounded-2xl border bg-background p-4">
              <Layers size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicislanden.build_your_island")}</p>
              <p className="text-xs text-muted-foreground">{t("publicislanden.buildings_decorations_residents")}</p>
            </div>
            <div className="rounded-2xl border bg-background p-4">
              <Users size={18} className="text-primary mb-2" />
              <p className="text-sm font-semibold mb-1">{t("publicislanden.share_it")}</p>
              <p className="text-xs text-muted-foreground">{t("publicislanden.just_like_the_one_you_re")}</p>
            </div>
          </div>

          {user ? (
            <a
              href="/recompenses"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:brightness-110 transition"
            >
              {t("publicislanden.view_my_island")} <ExternalLink size={14} />
            </a>
          ) : (
            <>
              <a
                href="/auth"
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white px-6 py-3 text-sm font-bold hover:brightness-110 transition"
              >
                <LogIn size={14} /> {t("publicislanden.create_my_island_for_free")}
              </a>
              <p className="text-xs text-muted-foreground mt-3">
                {t("publicislanden.7_days_free_no_credit_card")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}